// Dialog «Modifica» (#3): tutti i valori di una proposta NON ancora emessa.
// Guardia anche lato server: modificabile finché `document_number` è vuoto e lo
// stato non è fatturata/da_inviare — quindi anche le righe «in attesa», che
// prima mostravano il bottone ma prendevano 400 al salvataggio.
//
// I campi sono RAGGRUPPATI (documento · cliente · dati fiscali · indirizzo):
// erano quattordici input in un'unica griglia, cioè un muro in cui si cerca il
// campo a vista. I gruppi seguono l'ordine in cui si ragiona su una riga:
// cosa emetto → a chi → con quali dati fiscali → dove.
import { useEffect, useMemo, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { editProposal } from "../api";
import { DOC_TYPES_BY_KIND, type DocType, type EditBody, type Proposal } from "../types";

/** Campi testuali del form (doc_type e i rimborsi hanno controlli propri). */
type FieldKey =
  | "customer_name"
  | "country"
  | "gross_expected"
  | "invoice_date"
  | "company"
  | "vat_number"
  | "fiscal_code"
  | "ragione_sociale"
  | "sdi"
  | "pec"
  | "indirizzo"
  | "cap"
  | "citta"
  | "provincia"
  | "invoice_notes"
  | "refund_amount"
  | "refund_date";

interface Campo {
  key: FieldKey;
  label: string;
  type?: string;
  span2?: boolean;
  hint?: string;
}

interface Gruppo {
  titolo: string;
  campi: Campo[];
  /** Mostrato solo per le note di credito. */
  soloNc?: boolean;
}

const GRUPPI: Gruppo[] = [
  {
    titolo: "Documento",
    campi: [
      { key: "invoice_date", label: "Data fatturazione", type: "date" },
      { key: "gross_expected", label: "Importo lordo €", type: "number" },
    ],
  },
  {
    titolo: "Rimborso",
    soloNc: true,
    campi: [
      { key: "refund_amount", label: "Importo rimborsato €", type: "number" },
      { key: "refund_date", label: "Data rimborso", type: "date" },
    ],
  },
  {
    titolo: "Cliente",
    campi: [
      { key: "customer_name", label: "Cliente", span2: true },
      { key: "country", label: "Paese (ISO2)", hint: "Determina SF / OSS / estero" },
    ],
  },
  {
    titolo: "Dati fiscali",
    campi: [
      { key: "company", label: "Ragione sociale (dall'ordine)" },
      { key: "vat_number", label: "P.IVA" },
      { key: "fiscal_code", label: "Codice fiscale" },
      { key: "ragione_sociale", label: "Ragione sociale ufficiale", span2: true },
      { key: "sdi", label: "Codice SDI", hint: "7 caratteri · «0000000» non vale" },
      { key: "pec", label: "PEC" },
    ],
  },
  {
    titolo: "Indirizzo",
    campi: [
      { key: "indirizzo", label: "Indirizzo", span2: true },
      { key: "cap", label: "CAP" },
      { key: "citta", label: "Città" },
      { key: "provincia", label: "Prov." },
    ],
  },
];

function str(rec: Record<string, unknown> | null | undefined, key: string): string {
  const v = (rec ?? {})[key];
  return typeof v === "string" ? v : "";
}

function initialValues(p: Proposal): Record<FieldKey, string> {
  return {
    customer_name: p.customer_name ?? "",
    country: p.country ?? "",
    gross_expected: p.gross_expected != null ? String(p.gross_expected) : "",
    invoice_date: p.invoice_date ?? "",
    company: str(p.billing, "company"),
    vat_number: str(p.billing, "vat_number") || str(p.anagrafica, "piva"),
    fiscal_code: str(p.billing, "fiscal_code") || str(p.anagrafica, "cf"),
    ragione_sociale: str(p.anagrafica, "ragione_sociale"),
    sdi: str(p.anagrafica, "sdi"),
    pec: str(p.anagrafica, "pec"),
    indirizzo: str(p.anagrafica, "indirizzo"),
    cap: str(p.anagrafica, "cap"),
    citta: str(p.anagrafica, "citta"),
    provincia: str(p.anagrafica, "provincia"),
    invoice_notes: p.invoice_notes ?? "",
    refund_amount: p.refund_amount != null ? String(p.refund_amount) : "",
    refund_date: p.refund_date ?? "",
  };
}

const INPUT_CLS =
  "h-[38px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:opacity-50";

export function EditDialog({
  proposal,
  onDone,
  onClose,
}: {
  proposal: Proposal;
  onDone: () => void;
  onClose: () => void;
}) {
  const initial = useMemo(() => initialValues(proposal), [proposal]);
  const [values, setValues] = useState<Record<FieldKey, string>>(initial);
  const [docType, setDocType] = useState<string>(proposal.doc_type ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isNc = proposal.kind === "credit_note";
  const sigleAmmesse = DOC_TYPES_BY_KIND[isNc ? "credit_note" : "emission"];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const gruppiVisibili = GRUPPI.filter((g) => !g.soloNc || isNc);

  const submit = async () => {
    const body: EditBody = {};
    for (const g of gruppiVisibili) {
      for (const { key } of g.campi) {
        if (values[key] === initial[key]) continue; // solo i campi cambiati
        if (key === "gross_expected" || key === "refund_amount") {
          body[key] = values[key] === "" ? null : Number(values[key]);
        } else {
          body[key] = values[key];
        }
      }
    }
    // La nota è una textarea fuori dai gruppi: senza questo il loop sopra non la
    // vede e la scarta in silenzio (body vuoto => dialog chiuso, nota persa).
    if (values.invoice_notes !== initial.invoice_notes) {
      body.invoice_notes = values.invoice_notes;
    }
    if (docType && docType !== (proposal.doc_type ?? "")) {
      body.doc_type = docType as DocType;
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    if (body.country !== undefined) {
      // Normalizza PRIMA di validare/inviare: " it " passerebbe il check ma
      // prenderebbe un 422 dal backend (review acf0bda, minore UX).
      body.country = body.country.trim().toUpperCase();
      if (body.country.length !== 2) {
        setError("Il paese deve essere un codice ISO a 2 lettere (es. IT).");
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      await editProposal(proposal.id, body);
      onDone();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Modifica proposta"
      data-no-open
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg border border-line bg-card shadow-popover">
        <div className="border-b border-line px-5 py-4">
          <h3 className="font-display text-[16px] font-bold text-ink">
            Modifica — {proposal.customer_name}
          </h3>
          <p className="mt-1 text-[11.5px] leading-snug text-ink-2">
            Modificabile finché il documento non è stato emesso. I dati fiscali inseriti a mano
            valgono come verificati e fanno ricalcolare la sigla.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* La sigla è la prima decisione: sceglierla a mano è una decisione
              presa, quindi toglie la riga dalla revisione (lo fa il backend). */}
          <div className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            <label htmlFor="edit-doc_type">Sigla documento</label>
            <select
              id="edit-doc_type"
              aria-describedby="edit-doc_type-hint"
              value={docType}
              disabled={busy}
              onChange={(e) => setDocType(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">— decidi tu (resta la proposta automatica) —</option>
              {sigleAmmesse.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <span id="edit-doc_type-hint" className="text-[10.5px] font-normal text-ink-2">
              Sceglierla a mano toglie la riga dalla revisione: è una decisione presa.
            </span>
          </div>

          {gruppiVisibili.map((g) => (
            <fieldset key={g.titolo} className="mt-4 border-t border-line pt-3">
              <legend className="pr-2 text-[12px] font-semibold text-ink-2">{g.titolo}</legend>
              <div className="grid grid-cols-2 gap-3">
                {g.campi.map(({ key, label, type, span2, hint }) => (
                  // label/input collegati per id, hint via aria-describedby: se
                  // l'hint stesse DENTRO la <label> finirebbe nel nome
                  // accessibile del campo («Codice SDI7 caratteri · …»), che è
                  // sbagliato per uno screen reader.
                  <div
                    key={key}
                    className={`flex flex-col gap-1 text-[12px] font-medium text-ink-2 ${
                      span2 ? "col-span-2" : ""
                    }`}
                  >
                    <label htmlFor={`edit-${key}`}>{label}</label>
                    <input
                      id={`edit-${key}`}
                      aria-describedby={hint ? `edit-${key}-hint` : undefined}
                      type={type ?? "text"}
                      step={type === "number" ? "0.01" : undefined}
                      value={values[key]}
                      disabled={busy}
                      onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                      className={INPUT_CLS}
                    />
                    {hint && (
                      <span
                        id={`edit-${key}-hint`}
                        className="text-[10.5px] font-normal text-ink-2"
                      >
                        {hint}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          ))}

          <fieldset className="mt-4 border-t border-line pt-3">
            <legend className="pr-2 text-[12px] font-semibold text-ink-2">Nota in fattura</legend>
            <label
              htmlFor="edit-invoice_notes"
              className="mb-1 block text-[12px] font-medium text-ink-2"
            >
              Nota fattura (CUP / PNRR)
            </label>
            <textarea
              id="edit-invoice_notes"
              rows={3}
              value={values.invoice_notes}
              disabled={busy}
              onChange={(e) => setValues((prev) => ({ ...prev, invoice_notes: e.target.value }))}
              placeholder="Dicitura richiesta dal cliente in fattura (es. Progetto PNRR… CUP …)"
              className="w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 disabled:opacity-50"
            />
          </fieldset>

          {error && (
            <p role="alert" className="mt-3 text-[12px] text-red">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="rounded-md border border-accent bg-accent-2 px-3 py-2 text-[12px] font-medium text-white disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}
